import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    Alert,
    CircularProgress,
    Stack,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

const carouselData = [
    {
        title: "Live Tracking of Assets",
        description: "Continuous asset surveillance, ensuring constant oversight for enhanced security.",
        icon: "./images/fa-solid_route.svg",
        iconAlt: "Location Icon",
    },
    {
        title: "Continuous Asset Surveillance",
        description:
            "Our system provides continuous asset surveillance, ensuring constant oversight for enhanced security",
        icon: "./images/fa-solid_surveillance.svg",
        iconAlt: "Surveillance Icon",
    },
    {
        title: "Efficient Fleet Care, Reliable Fleet Operations",
        description:
            "Real-time insights into the status and performance of mining assets allow for proactive management, ensuring optimal efficiency and minimizing downtime for unparalled productivity.",
        icon: "./images/fa-solid_operations.svg",
        iconAlt: "Operations Icon",
    },
];

const LeftHeroCard = ({ activeIndex, setIndex }) => {
    return (
        <Box
            sx={{
                height: "100vh",
                width: '100%',
                px: { xs: 2, md: 3 },
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: "90%",
                    height: "calc(100vh - 30px)",
                    borderRadius: "22px",
                    overflow: "hidden",
                    p: { xs: 3, md: 7 },
                    backgroundImage: "linear-gradient(180deg, #2061F5 0%, #13398F 100%)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxSizing: "border-box",
                }}
            >
                <Box>
                    <Box
                        sx={{ display: "flex", alignItems: "center", mb: { xs: 2, md: 4 } }}
                    >
                        <img src="/images/cog_white_logo.svg" alt="cognecto" width={300} />
                    </Box>

                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            color: "#fff",
                            lineHeight: 1.03,
                            mb: 2,
                            fontSize: { xs: "1.8rem", md: "2.6rem" },
                            fontFamily: 'Poppins, "Helvetica Neue", sans-serif',
                        }}
                    >
                        Trusted by industrial leaders for Operational Excellence.
                    </Typography>

                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.92)",
                            mt: 1,
                            maxWidth: 720,
                            fontSize: 15.5,
                        }}
                    >
                        Unlock real-time insights to transform operations with sustainable
                        practices and operational excellence.
                    </Typography>
                </Box>

                <Box sx={{ mb: { xs: 2, md: 4 } }}>
                    <Paper
                        elevation={6}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.08)",
                            p: { xs: 2, md: 3 },
                            borderRadius: "18px",
                            width: "100%",
                            boxSizing: "border-box",
                            backdropFilter: "saturate(120%) blur(4px)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <img
                                src={carouselData[activeIndex].icon}
                                alt={carouselData[activeIndex].iconAlt}
                                style={{ width: 56, height: 56, objectFit: "contain" }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                        color: "#fff",
                                        fontFamily: 'Poppins, "Helvetica Neue", sans-serif',
                                    }}
                                >
                                    {carouselData[activeIndex].title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        opacity: 0.95,
                                        color: "rgba(255,255,255,0.95)",
                                    }}
                                >
                                    {carouselData[activeIndex].description}
                                </Typography>
                            </Box>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 1, justifyContent: "flex-start", alignItems: "center" }}
                        >
                            {carouselData.map((_, i) => (
                                <Box
                                    key={i}
                                    onClick={() => typeof setIndex === "function" && setIndex(i)}
                                    sx={{
                                        width: i === activeIndex ? 44 : 28,
                                        height: 6,
                                        bgcolor:
                                            i === activeIndex
                                                ? "rgba(255,255,255,0.92)"
                                                : "rgba(255,255,255,0.18)",
                                        borderRadius: 3,
                                        cursor: "pointer",
                                        transition: "width 180ms ease",
                                    }}
                                    role="button"
                                    aria-label={`carousel-dot-${i + 1}`}
                                />
                            ))}
                        </Stack>
                    </Paper>
                </Box>
            </Paper>
        </Box>
    );
};



export default function LoginPage({ onLoginSuccess }) {
    const { login } = useAuth();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

    const [index, setIndex] = useState(0);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const id = setInterval(
            () => setIndex((i) => (i + 1) % carouselData.length),
            4000
        );
        return () => clearInterval(id);
    }, []);

    const onSubmit = async (e) => {
        e?.preventDefault();
        setError("");
        if (!username.trim() || !password) {
            setError("Please enter username and password.");
            return;
        }
        setBusy(true);
        try {
            await login(username.trim(), password);
            navigate('/');
            if (onLoginSuccess) {
                onLoginSuccess();
                window.location.reload();
            }
        } catch (err) {
            let serverMessage = err?.message || "";
            if (!serverMessage && err?.data) {
                if (typeof err.data === "string") {
                    const match = err.data.match?.(/<message>(.*?)<\/message>/);
                    serverMessage = match?.[1] || err.data;
                } else if (typeof err.data === "object") {
                    serverMessage =
                        err.data.message || err.data.error || JSON.stringify(err.data);
                }
            }
            if (!serverMessage) serverMessage = "Invalid username or password.";
            setError(serverMessage);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Grid
            container
            wrap="nowrap"
            sx={{
                minHeight: "100vh",
                alignItems: "center",
                fontFamily: 'Poppins, "Helvetica Neue", sans-serif',
                background: "#fbfbfb",
                width: '100%',
            }}
        >
            {isMdUp && (
                <Grid item xs={12} md={6} sx={{ display: { xs: "none", md: "block" }, width: '55%', }}>
                    <LeftHeroCard activeIndex={index} setIndex={setIndex} />
                </Grid>
            )}

            <Grid
                item
                xs={12}
                md={isMdUp ? 6 : 12}
                component={Paper}
                elevation={0}
                square
                sx={{
                    display: "flex",
                    alignItems: isMdUp ? "flex-start" : "center",
                    justifyContent: "center",
                    background: "#fff",
                    p: { xs: 3, md: 8 },
                    width: '40%'
                }}
            >
                <Box sx={{ width: "80%", gap: 4 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                            Sign in
                        </Typography>
                        <Typography color="text.secondary">
                            Please login to continue to your account.
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={onSubmit} noValidate>
                        <TextField
                            fullWidth
                            size="small"
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{
                                mb: 2,
                                "& .MuiInputBase-root": {
                                    bgcolor: "#f6f9ff",
                                    borderRadius: 1.5,
                                },
                            }}
                            autoFocus
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{
                                mb: 1.5,
                                "& .MuiInputBase-root": {
                                    bgcolor: "#f6f9ff",
                                    borderRadius: 1.5,
                                },
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                                gap: 2,
                            }}
                        >
                            <Link
                                href="#"
                                underline="none"
                                onClick={(e) => e.preventDefault()}
                                sx={{ fontSize: 13 }}
                            >
                                forgot password ?
                            </Link>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={busy}
                            sx={{
                                backgroundColor: "#1E5FD7",
                                color: "#fff",
                                "&:hover": { backgroundColor: "#124ea0" },
                                borderRadius: 2,
                                boxShadow: "none",
                                mb: 2,
                                height: 48,
                                fontWeight: 700,
                            }}
                            aria-label="sign-in"
                        >
                            {busy ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                "Sign In"
                            )}
                        </Button>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 4, mt: 2 }}>
                        <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
                        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                            or
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
                    </Box>

                    <Typography sx={{ mt: 2, fontSize: 13 }}>
                        Need an account?{" "}
                        <Link href="#" onClick={(e) => e.preventDefault()}>
                            connect
                        </Link>
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );
}
